import { Formik, FormikHelpers } from "formik";
import {
  HeaderSaveFormButton,
  ReportingMelcoMachineStatisticsModelsGroup,
  ReportingMelcoMachineStatisticsModelsMachineListItem,
  useFormSubmit,
} from "melco-shared-logic";
import { DisplayMachineGroupDetailForm } from "./DisplayMachineGroupDetailForm";
import { useStatisticsGroupsEdit } from "../../../hooks/reporting/useStatisticsGroupsEdit";
import { useStatisticsGroupsAdd } from "../../../hooks/reporting/useStatisticsGroupsAdd";
import { DirtyFormPrompt } from "../../form/DirtyFormPrompt";
import { useNavigate, useParams } from "react-router";
import { useState } from "react";
import LoadingDefaultSpinIndicator from "../../loading/LoadingDefaultSpinIndicator";
import { QueryObserverResult } from "react-query";

export type MachineGroupDetailFormValueType = {
  name: string;
  id: string;
  machine_list?: Array<ReportingMelcoMachineStatisticsModelsMachineListItem> | null;
};

const emptyValues = {
  name: "",
  id: "",
  machine_list: [],
};

export const machineGroupToInitialValues = (
  machineGroup: ReportingMelcoMachineStatisticsModelsGroup | undefined
) => {
  const { name, id, machine_list } = machineGroup ?? emptyValues;

  const initialValues: MachineGroupDetailFormValueType = {
    name: name ?? "",
    id: id ?? "",
    machine_list: machine_list ?? [],
  };

  return initialValues;
};

type MachineGroupDetailFormProps = {
  machineGroup?: ReportingMelcoMachineStatisticsModelsGroup | undefined;
  refetch: () => Promise<QueryObserverResult<ReportingMelcoMachineStatisticsModelsGroup | undefined, unknown>>
};

export const MachineGroupDetailForm: React.FC<MachineGroupDetailFormProps> = ({
  machineGroup,
  refetch
}) => {
  const initialValues = machineGroupToInitialValues(machineGroup);
  const { machineGroupId } = useParams<{ machineGroupId: string }>();

  const [isLoading, setIsLoading] = useState(false);
  const editMachineGroup = useStatisticsGroupsEdit();

  const addMachineGroup = useStatisticsGroupsAdd();

  const navigate = useNavigate();

  const onHandleSubmit = useFormSubmit<MachineGroupDetailFormValueType>(
    async (
      values: MachineGroupDetailFormValueType,
      formikHelpers: FormikHelpers<MachineGroupDetailFormValueType>
    ) => {
      if (machineGroup && machineGroup.id) {
        const newEntity = await editMachineGroup(values);
        refetch?.();
        // optimistically update ui
        formikHelpers.resetForm({
          values: machineGroupToInitialValues(newEntity),
        });
      } else {
        const newEntity = await addMachineGroup(values);


        // Explicitly resetting the form,
        formikHelpers.resetForm({ values: initialValues });
        // redirect: open new created group to groups edit mode
        // delay for 1 seconds to get reset the form so that dirty get false
        setTimeout(() => {
          setIsLoading(false);
          navigate("/reporting/machine/groups/" + newEntity?.id);
        }, 10);
      }
    },
    {
      translationPrefix: "",
    }
  );

  if (!machineGroup && !(machineGroupId == "new")) {
    return null;
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, formikHelpers) =>
        onHandleSubmit(values, formikHelpers, {
          isManuallyResettingForm: true,
        })
      }
    >
      <>
        <DirtyFormPrompt>
          <LoadingDefaultSpinIndicator isShown={isLoading}>
            <DisplayMachineGroupDetailForm />
            <HeaderSaveFormButton />
          </LoadingDefaultSpinIndicator>
        </DirtyFormPrompt>
      </>
    </Formik>
  );
};
